import { api } from './api';
import { isIos, isStandalone } from './install';

/**
 * Turning on notifications for this device.
 *
 * iOS only allows them once the portal has been added to the home screen, so
 * `supported` is false in mobile Safari until that happens — the UI says so
 * rather than offering a button that cannot work.
 */

export type PushState = 'unsupported' | 'needs-install' | 'off' | 'on' | 'blocked';

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'blocked';

  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  return existing ? 'on' : 'off';
}

function toUint8(base64url: string): Uint8Array {
  const padded = (base64url + '==='.slice((base64url.length + 3) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function enablePush(): Promise<PushState> {
  const { key } = await api<{ key: string | null }>('/admin/push/key');
  if (!key) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';

  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toUint8(key) as BufferSource,
  });

  await api('/admin/push/subscribe', {
    method: 'POST',
    body: { endpoint: subscription.endpoint },
  });

  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return 'off';

  await api('/admin/push/subscribe', {
    method: 'DELETE',
    body: { endpoint: subscription.endpoint },
  }).catch(() => undefined);

  await subscription.unsubscribe();
  return 'off';
}
